import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../lib/cn';

export type TextFieldType = 'text' | 'email' | 'password';

export interface TextFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange' | 'value' | 'id'> {
  id?: string;
  label: ReactNode;
  type?: TextFieldType;
  value: string;
  onChange: (value: string) => void;
  /** Shown below the field in the danger accent — also drives the invalid border/label state. */
  error?: string;
  description?: ReactNode;
  className?: string;
}

/**
 * Labelled text input — the entry/login/registration flow's basic unit,
 * first text input in the design system (everything before this was
 * buttons/toggles/tabs). Layout mirrors `Switch`'s label/description
 * convention for visual consistency across Settings-style forms.
 */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { id, label, type = 'text', value, onChange, error, description, className, ...rest },
  ref,
) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const errorId = error ? `${fieldId}-error` : undefined;
  const descriptionId = description ? `${fieldId}-description` : undefined;

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <label htmlFor={fieldId} className="text-sm font-medium text-content">
        {label}
      </label>
      {description ? (
        <p id={descriptionId} className="-mt-0.5 text-xs leading-relaxed text-content-muted">
          {description}
        </p>
      ) : null}
      <input
        ref={ref}
        id={fieldId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={error ? true : undefined}
        aria-describedby={[errorId, descriptionId].filter(Boolean).join(' ') || undefined}
        className={cn(
          'h-9 rounded-control border bg-surface-3 px-3 text-sm text-content',
          'placeholder:text-content-muted',
          'transition-colors duration-[var(--sg-duration-fast)] ease-hud',
          'focus:outline-none focus:shadow-glow-sm',
          'disabled:pointer-events-none disabled:opacity-45',
          error
            ? 'border-danger/70 focus:border-danger'
            : 'border-edge focus:border-silicate/70',
        )}
        {...rest}
      />
      {error ? (
        <p id={errorId} className="text-xs text-danger-bright">
          {error}
        </p>
      ) : null}
    </div>
  );
});
